import { Box, Typography, type SxProps, type Theme } from "@mui/material";
import type { ReactNode } from "react";

/** Yellow neon CSS tokens — reused across hero, eyebrows, dividers, CTAs. */
export const NEON_GLOW =
  "0 0 8px rgba(255,176,0,0.9), 0 0 22px rgba(255,176,0,0.55), 0 0 40px rgba(255,176,0,0.3)";
export const NEON_GLOW_SOFT =
  "0 0 6px rgba(255,176,0,0.7), 0 0 18px rgba(255,176,0,0.4)";
export const NEON_STRIPE =
  "linear-gradient(90deg, #ffb000 0%, #ff8800 50%, transparent 100%)";

interface AccentProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
  /** Add a soft neon glow. Default off — reserve for top-level section openers and the page hero. */
  glow?: boolean;
}

/**
 * Brand-amber eyebrow — use above section h2s. Default is a quiet amber label;
 * pass `glow` for top-level section openers (hero, major page sections).
 */
export function NeonEyebrow({ children, sx, glow = false }: AccentProps) {
  return (
    <Typography
      variant="overline"
      sx={{
        color: "primary.main",
        letterSpacing: 3,
        fontWeight: 700,
        lineHeight: 1,
        display: "inline-block",
        ...(glow && { textShadow: NEON_GLOW_SOFT }),
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

interface DividerProps {
  /** Width in px, default fills container with `100%`. */
  width?: number | string;
  sx?: SxProps<Theme>;
}

/**
 * Neon-glowing horizontal stripe — section divider.
 */
export function NeonDivider({ width = "100%", sx }: DividerProps) {
  return (
    <Box
      sx={{
        height: 3,
        width,
        background: NEON_STRIPE,
        boxShadow: NEON_GLOW,
        borderRadius: 2,
        ...sx,
      }}
    />
  );
}

interface SectionHeadingProps {
  eyebrow: string;
  title: string;
  align?: "left" | "center";
  /** Show the neon stripe under the eyebrow. */
  stripe?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Standard section-opener: glowing eyebrow + bold h2. Used at the top of
 * Home sections, archive pages, list pages.
 */
export function SectionHeading({ eyebrow, title, align = "left", stripe, sx }: SectionHeadingProps) {
  return (
    <Box sx={{ mb: 3, textAlign: align, ...sx }}>
      <NeonEyebrow glow>{eyebrow}</NeonEyebrow>
      {stripe && (
        <NeonDivider width={80} sx={{ mt: 1, mx: align === "center" ? "auto" : 0 }} />
      )}
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: 28, md: 36 },
          fontWeight: 900,
          mt: 1,
          letterSpacing: "-0.01em",
          lineHeight: 1.1,
          color: "text.primary",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

/**
 * Neon-button sx mixin — apply to a `<Button variant="contained">` to give
 * it a glowing border on hover. Returns an `sx` object you can spread.
 */
export const neonButtonSx: SxProps<Theme> = {
  position: "relative",
  fontWeight: 800,
  letterSpacing: 0.5,
  boxShadow: "0 0 0 0 rgba(255,176,0,0)",
  transition: "box-shadow 200ms ease, transform 150ms ease",
  "&:hover": {
    boxShadow:
      "0 0 0 1px rgba(255,176,0,0.9), 0 0 16px rgba(255,176,0,0.55), 0 0 36px rgba(255,176,0,0.3)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
};
